import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { DeleteButton } from '@/components/custom/delete-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAssignmentRule,
  useCreateAssignmentRule,
  useUpdateAssignmentRule,
  useDeleteAssignmentRule,
} from '@/hooks/use-assignment-rules';
import { useServerFieldErrors } from '@/hooks/use-server-field-errors';
import { CATCH_ALL_ALLOWED_ASSIGNEES, RULE_KIND } from '@vagusx/clients';
import type { AssignmentRuleNode } from '@vagusx/shared';
import {
  ConditionBuilder,
  areAllLeavesConfigured,
  type EditorNode,
} from '@/components/custom/condition-builder';
import { AssigneePicker } from '@/components/custom/assignee-picker';
import type { AssigneeValue } from '@/components/custom/assignee-picker';

/**
 * Canonical empty root group — what a new (non-catch-all) rule starts
 * with. Shape is `EditorNode` (the editor-internal type that allows
 * partial leaves); structurally identical to the `AssignmentRuleNode`
 * wire shape for empty groups, so the cast at save time is safe.
 */
const EMPTY_ROOT: EditorNode = { kind: 'group', op: 'all', children: [] };

type UiField = 'name' | 'description' | 'conditions' | 'assignee';

/**
 * Map server-side field names to the UI section that renders the
 * inline error. Fields without a visible section (e.g. `ruleKind`)
 * are intentionally absent — `useServerFieldErrors` routes unmapped
 * fields to the top-level banner.
 */
const FIELD_TO_UI: Record<string, UiField> = {
  name: 'name',
  description: 'description',
  conditions: 'conditions',
  assignToType: 'assignee',
  assignToId: 'assignee',
};

interface RuleEditorProps {
  ruleId: string | undefined;
  /**
   * When set, the editor opens in catch-all create mode: conditions
   * section hidden, assignee picker restricted to user/team, name
   * pre-filled with a sensible default. Ignored when ruleId is set —
   * for an existing rule we derive the variant from `ruleKind` on the
   * loaded data.
   */
  initialIsCatchAll?: boolean;
  onClose: () => void;
}

export function RuleEditor({
  ruleId,
  initialIsCatchAll,
  onClose,
}: RuleEditorProps): React.ReactElement {
  const isEdit = !!ruleId;
  const { data: existingRule, isLoading } = useAssignmentRule(ruleId);
  const createMutation = useCreateAssignmentRule();
  const updateMutation = useUpdateAssignmentRule();
  const deleteMutation = useDeleteAssignmentRule();

  // Effective catch-all flag: editing reads it from the loaded row's
  // ruleKind; creating reads it from the prop (set by the page when
  // navigating from "Add default" on the catch-all card).
  const isCatchAll = isEdit ? existingRule?.ruleKind === RULE_KIND.CATCH_ALL : !!initialIsCatchAll;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [conditions, setConditions] = useState<EditorNode>(EMPTY_ROOT);
  const [assignee, setAssignee] = useState<AssigneeValue | null>(null);
  const [isEnabled, setIsEnabled] = useState(true);
  // Toggled true on the first save attempt with incomplete conditions;
  // surfaces inline error states under each missing field/operator/
  // value cell. Cleared when conditions are fully completed.
  const [showConditionErrors, setShowConditionErrors] = useState(false);

  const errors = useServerFieldErrors<UiField>(FIELD_TO_UI);

  useEffect(() => {
    if (isEdit && existingRule) {
      setName(existingRule.name);
      setDescription(existingRule.description ?? '');
      setConditions(existingRule.conditions);
      setAssignee({
        type: existingRule.assignToType as 'user' | 'agent' | 'team',
        id: existingRule.assignToId,
        name: existingRule.assignToName ?? '',
      });
      setIsEnabled(existingRule.isEnabled);
    } else if (!isEdit) {
      setName(initialIsCatchAll ? 'Default assignment' : '');
      setDescription('');
      setConditions(EMPTY_ROOT);
      setAssignee(null);
      setIsEnabled(true);
    }
  }, [isEdit, existingRule, initialIsCatchAll]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!assignee) return;

      // Guard regular rules at the client layer: every leaf must have
      // field, operator, AND non-empty value before we hit the API. If
      // anything's missing, flip on inline error rendering and bail.
      // The catch-all path skips this check (its conditions are an
      // empty root by design).
      if (!isCatchAll && !areAllLeavesConfigured(conditions)) {
        setShowConditionErrors(true);
        return;
      }
      setShowConditionErrors(false);
      errors.reset();

      const baseData = {
        name,
        description: description || null,
        assignToType: assignee.type,
        assignToId: assignee.id,
        isEnabled,
      };

      // Cast EditorNode → AssignmentRuleNode at the wire boundary.
      // EditorNode is structurally a superset (allows null
      // field/operator on placeholder leaves); the validity gate above
      // guarantees no nulls or empty values reach this point. The
      // server's Zod schema is the authoritative check — if anything
      // slips through, the request fails fast with a `conditions` error.
      const wireConditions = (isCatchAll
        ? EMPTY_ROOT
        : conditions) as unknown as AssignmentRuleNode;

      try {
        if (isEdit && ruleId) {
          // Catch-all conditions are forced to an empty root group
          // server-side; sending one explicitly here keeps client +
          // server in sync.
          await updateMutation.mutateAsync({
            id: ruleId,
            data: { ...baseData, conditions: wireConditions },
          });
        } else {
          await createMutation.mutateAsync({
            ...baseData,
            conditions: wireConditions,
            ruleKind: isCatchAll ? RULE_KIND.CATCH_ALL : RULE_KIND.REGULAR,
          });
        }
        onClose();
      } catch (err) {
        // Surface validation/duplicate errors inline next to the field
        // they came from. The global toast still fires from the
        // MutationCache.onError handler — this is additive so the user
        // sees both a top-level summary AND the specific field hint.
        errors.capture(err);
      }
    },
    [
      name,
      description,
      conditions,
      assignee,
      isEnabled,
      isCatchAll,
      isEdit,
      ruleId,
      onClose,
      updateMutation,
      createMutation,
      errors,
    ]
  );

  const handleDelete = async () => {
    if (!ruleId) return;
    await deleteMutation.mutateAsync(ruleId);
    onClose();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEdit && isLoading) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-3 border-b px-6 py-4">
          <Skeleton className="h-6 w-6" />
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="flex-1 space-y-4 p-6">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      {/* Header — title varies by edit mode + catch-all flag so the
       * user always knows what they're working on. */}
      <div className="flex items-center border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold">
            {isCatchAll
              ? isEdit
                ? 'Edit Default Assignment'
                : 'Set Default Assignment'
              : isEdit
                ? 'Edit Rule'
                : 'Create Rule'}
          </h2>
        </div>
      </div>

      {/* Content */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl space-y-8 p-6">
          {/* Top-level form error — shown when the server returned a
           * validation error not tied to a visible field. */}
          {errors.topError && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errors.topError}</span>
            </div>
          )}

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                errors.clearField('name');
              }}
              placeholder={
                isCatchAll
                  ? 'e.g., Default assignment'
                  : 'e.g., Route urgent Aetna documents to Team A'
              }
              required
              aria-invalid={!!errors.byField.name}
            />
            {errors.byField.name && (
              <p className="text-sm text-destructive">{errors.byField.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                errors.clearField('description');
              }}
              placeholder="Optional description of what this rule does"
              rows={2}
              aria-invalid={!!errors.byField.description}
            />
            {errors.byField.description && (
              <p className="text-sm text-destructive">{errors.byField.description}</p>
            )}
          </div>

          {/* Conditions — hidden for catch-all; the engine always
           * matches it as the fallback so conditions are meaningless. */}
          {!isCatchAll && (
            <div className="space-y-3">
              <div>
                <Label>Conditions</Label>
                <p className="mt-1 text-sm text-muted-foreground">
                  When a task matches these conditions, it will be automatically assigned.
                </p>
              </div>
              <ConditionBuilder
                root={conditions}
                showErrors={showConditionErrors}
                onChange={(next) => {
                  setConditions(next);
                  errors.clearField('conditions');
                  // As soon as the user finishes filling in everything
                  // that was missing, drop the inline error state so
                  // the editor goes quiet again.
                  if (showConditionErrors && areAllLeavesConfigured(next)) {
                    setShowConditionErrors(false);
                  }
                }}
              />
              {errors.byField.conditions && (
                <p className="text-sm text-destructive">{errors.byField.conditions}</p>
              )}
            </div>
          )}

          {/* Assignment — catch-all uses the shared CATCH_ALL_ALLOWED_ASSIGNEES
           * allow-list so server validation, this picker, and the card
           * icon stay in sync. */}
          <div className="rounded-lg bg-muted/50 p-5">
            <div className="space-y-2">
              <Label>{isCatchAll ? 'Default assignee' : 'Then assign to'}</Label>
              {isCatchAll && (
                <p className="text-sm text-muted-foreground">
                  Tasks that don't match any rule above will be assigned here.
                </p>
              )}
              <div className="max-w-sm">
                <AssigneePicker
                  selected={assignee}
                  onChange={(v) => {
                    setAssignee(v);
                    errors.clearField('assignee');
                  }}
                  allowedTypes={
                    isCatchAll ? [...CATCH_ALL_ALLOWED_ASSIGNEES] : ['user', 'agent', 'team']
                  }
                />
              </div>
              {errors.byField.assignee && (
                <p className="text-sm text-destructive">{errors.byField.assignee}</p>
              )}
            </div>
          </div>

          {/* Active toggle — disabling pauses the rule (or the
           * catch-all fallback) without deleting it. */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="is-enabled">Active</Label>
              <p className="text-sm text-muted-foreground">
                {isCatchAll
                  ? 'When off, unmatched tasks stay unassigned.'
                  : 'When off, this rule is skipped during evaluation.'}
              </p>
            </div>
            <Switch id="is-enabled" checked={isEnabled} onCheckedChange={setIsEnabled} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 border-t px-6 py-3">
        {isEdit && (
          <DeleteButton size="sm" isPending={deleteMutation.isPending} onClick={handleDelete} />
        )}
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {(() => {
          // Validation: catch-all skips condition checks; regular rules
          // need at least one well-formed condition leaf anywhere in
          // the tree.
          // Submit gate is intentionally not blocked by an incomplete
          // conditions tree — clicking save is the trigger that
          // surfaces inline error states (handleSubmit checks
          // `areAllLeavesConfigured` and bails early). Disabling the
          // button would hide where the missing pieces are, defeating
          // the in-context feedback we want.
          const disabled = isPending || !assignee;
          if (isEdit) {
            return <SaveButton type="submit" isPending={isPending} isEdit disabled={disabled} />;
          }
          return (
            <Button type="submit" disabled={disabled}>
              {isPending ? 'Saving...' : isCatchAll ? 'Save Default' : 'Create Rule'}
            </Button>
          );
        })()}
      </div>
    </form>
  );
}
